import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bot } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { appTexts } from "@/i18n/appTexts";
import { BackArrow } from "@/components/BackArrow";

export default function Login() {
  const { login } = useAuth();
  const { lang, setLang } = useLang();
  const t = appTexts[lang].auth;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(u?.role === "superadmin" ? "/superadmin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-15%] w-[400px] h-[400px] bg-[#B026FF] blur-[150px] opacity-25 rounded-full" />
      <div className="absolute bottom-[-15%] right-[-10%] w-[400px] h-[400px] bg-[#00F5FF] blur-[150px] opacity-15 rounded-full" />
      <div className="absolute top-6 left-6 z-20"><BackArrow /></div>
      <div className="absolute top-6 right-6 flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
        {["en", "es"].map((l) => (
          <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
            className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${lang === l ? "bg-[#B026FF] text-white" : "text-white/50"}`}>{l}</button>
        ))}
      </div>

      <div className="relative w-full max-w-md bg-[#0F0F1A]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 z-10">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6 font-heading font-black text-lg">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#B026FF] to-[#00F5FF] flex items-center justify-center"><Bot size={18} /></span>
          AVATAR <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B026FF] to-[#00F5FF]">4 U</span>
        </Link>
        <h1 className="font-heading text-2xl font-black text-center">{t.loginTitle}</h1>
        <p className="text-sm text-[#A0A0B0] text-center mb-8">{t.loginSub}</p>

        <form onSubmit={submit} className="space-y-4">
          <input data-testid="login-email-input" type="text" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={lang === "es" ? "Correo o celular (10 dígitos)" : "Email or cell phone (10 digits)"}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B026FF]/60 placeholder:text-white/30" />
          <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t.password}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B026FF]/60 placeholder:text-white/30" />
          {error && <p data-testid="login-error" className="text-red-400 text-xs">{error}</p>}
          <button data-testid="login-submit-btn" disabled={busy} type="submit"
            className="w-full py-3.5 rounded-full font-bold text-sm uppercase tracking-widest bg-[#B026FF] shadow-[0_0_20px_rgba(176,38,255,0.45)] hover:shadow-[0_0_30px_rgba(176,38,255,0.7)] transition-shadow disabled:opacity-60">
            {busy ? "..." : t.loginBtn}
          </button>
        </form>
        <p className="text-xs text-[#A0A0B0] text-center mt-6">
          {t.noAccount}{" "}
          <Link to="/register" data-testid="goto-register-link" className="text-[#00F5FF] font-bold hover:underline">{t.signupLink}</Link>
        </p>
      </div>
    </main>
  );
}
